import { Fragment } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/cn.js";

export interface BreadcrumbItem {
  label: string;
  to?: string;
  /** Marks the current page (not a link). */
  current?: boolean;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

function ChevronSeparator() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0 text-content-muted/70"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0", className)}>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isCurrent = item.current ?? isLast;

          return (
            <Fragment key={`${item.label}-${index}`}>
              {index > 0 && (
                <li className="flex items-center" aria-hidden>
                  <ChevronSeparator />
                </li>
              )}
              <li className="min-w-0">
                {item.to && !isCurrent ? (
                  <Link
                    to={item.to}
                    className="truncate text-content-secondary transition-colors hover:text-accent focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className={cn(
                      "truncate",
                      isCurrent ? "font-medium text-content-primary" : "text-content-secondary"
                    )}
                    aria-current={isCurrent ? "page" : undefined}
                  >
                    {item.label}
                  </span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
